import { http } from '../request'
import type {
    AccountCreateRequest,
    AccountUpdateRequest,
    AccountListItem,
    AccountListRequest,
    AccountSearchRequest,
    AccountListResponse,
    AccountDetailResponse,
    AccountCreateResponse,
    AccountUpdateResponse,
    AccountDeleteResponse,
    AccountSubmitAuditResponse,
    AccountCancelAuditResponse,
    AccountUnpublishResponse,
    AccountSearchResponse,
    AccountStatsResponse,
    AccountAutoFetchPreviewRequest,
    AccountAutoFetchPreviewResponse,
    AccountAutoFetchAuthStartResponse,
    AccountAutoFetchAuthStatusResponse,
    AccountAutoFetchSessionStartResponse,
    AccountAutoFetchSessionStatusResponse
} from '../types/accounts'

/**
 * 游戏账号相关 API
 */
export const accountsApi = {
    /**
     * 获取账号列表
     * GET /api/accounts/
     */
    getAccountList(params?: AccountListRequest): Promise<AccountListResponse> {
        return http.list<AccountListItem>('/api/accounts/', params)
    },

    /**
     * 发布前预校验
     * POST /api/accounts/{id}/pre-validate/
     */
    preValidate(id: number): Promise<void> {
        return http.post<void>(`/api/accounts/${id}/pre-validate/`)
    },

    /**
     * 创建账号
     * POST /api/accounts/
     */
    createAccount(data: AccountCreateRequest): Promise<AccountCreateResponse> {
        return http.post<AccountCreateResponse>('/api/accounts/', data)
    },

    /**
     * 获取账号详情
     * GET /api/accounts/{id}/
     */
    getAccountDetail(id: number): Promise<AccountDetailResponse> {
        return http.get<AccountDetailResponse>(`/api/accounts/${id}/`)
    },

    /**
     * 更新账号
     * PUT /api/accounts/{id}/
     */
    updateAccount(id: number, data: AccountUpdateRequest): Promise<AccountUpdateResponse> {
        return http.put<AccountUpdateResponse>(`/api/accounts/${id}/`, data)
    },

    /**
     * 删除账号
     * DELETE /api/accounts/{id}/
     */
    deleteAccount(id: number): Promise<AccountDeleteResponse> {
        return http.delete<AccountDeleteResponse>(`/api/accounts/${id}/`)
    },

    /**
     * 提交审核
     * POST /api/accounts/{id}/submit-for-audit/
     */
    submitForAudit(id: number): Promise<AccountSubmitAuditResponse> {
        return http.post<AccountSubmitAuditResponse>(`/api/accounts/${id}/submit-for-audit/`)
    },

    /**
     * 撤销审核
     * POST /api/accounts/{id}/cancel-audit/
     */
    cancelAudit(id: number): Promise<AccountCancelAuditResponse> {
        return http.post<AccountCancelAuditResponse>(`/api/accounts/${id}/cancel-audit/`)
    },

    /**
     * 下架账号
     * POST /api/accounts/{id}/unpublish/
     */
    unpublishAccount(id: number): Promise<AccountUnpublishResponse> {
        return http.post<AccountUnpublishResponse>(`/api/accounts/${id}/unpublish/`)
    },

    /**
     * 搜索账号（市场公开接口）
     * GET /api/market/accounts/
     */
    searchAccounts(params?: AccountSearchRequest): Promise<AccountSearchResponse> {
        return http.list<AccountListItem>('/api/market/accounts/', params)
    },

    /**
     * 获取账号统计信息
     * GET /api/accounts/statistics/
     */
    getAccountStats(): Promise<AccountStatsResponse> {
        return http.get<AccountStatsResponse>('/api/accounts/statistics/')
    },

    /**
     * 自动拉取三角洲数据预览（只返回候选，前端确认后再写入表单）
     * POST /api/accounts/auto-fetch/preview/
     */
    autoFetchPreview(data: AccountAutoFetchPreviewRequest): Promise<AccountAutoFetchPreviewResponse> {
        return http.post<AccountAutoFetchPreviewResponse>('/api/accounts/auto-fetch/preview/', data)
    },

    /**
     * 创建三角洲业务授权跳转会话
     * POST /api/accounts/auto-fetch/auth/start/
     */
    startAutoFetchAuth(): Promise<AccountAutoFetchAuthStartResponse> {
        return http.post<AccountAutoFetchAuthStartResponse>('/api/accounts/auto-fetch/auth/start/', {})
    },

    /**
     * 查询三角洲业务授权跳转结果
     * GET /api/accounts/auto-fetch/auth/{sessionId}/status/
     */
    getAutoFetchAuthStatus(sessionId: string): Promise<AccountAutoFetchAuthStatusResponse> {
        return http.get<AccountAutoFetchAuthStatusResponse>(`/api/accounts/auto-fetch/auth/${sessionId}/status/`, { timeout: 180000 })
    },

    /**
     * 创建自建三角洲扫码授权会话
     * POST /api/accounts/auto-fetch/session/start/
     */
    startAutoFetchSession(): Promise<AccountAutoFetchSessionStartResponse> {
        return http.post<AccountAutoFetchSessionStartResponse>('/api/accounts/auto-fetch/session/start/', {})
    },

    /**
     * 查询自建三角洲扫码授权状态
     * GET /api/accounts/auto-fetch/session/{sessionId}/status/
     */
    getAutoFetchSessionStatus(sessionId: string): Promise<AccountAutoFetchSessionStatusResponse> {
        return http.get<AccountAutoFetchSessionStatusResponse>(`/api/accounts/auto-fetch/session/${sessionId}/status/`, { timeout: 180000 })
    }
}

export default accountsApi 
